import jsQR from 'jsqr'

/** What the customer answered last time, so a returning visitor re-types less. */
export type CustomerQrLastBooking = {
  remark: string
  isExternal: boolean
  attendedAddress: string
  customForm: Record<string, unknown>
}

/** The limited customer shape the QR resolve endpoint returns. */
export type CustomerQr = {
  customerId: string
  name: string
  phoneNo: string
  email: string
  address: string
  lastBooking?: CustomerQrLastBooking
}

/** Prefixes the phone `<select>` offers. Longest first, so `+62` never matches before a longer code. */
const KNOWN_PHONE_PREFIXES = ['+62']

/**
 * Splits a stored phone number into the prefix the form's dropdown holds and the
 * national part typed into the input.
 *
 * Stored numbers are inconsistent — `+628123`, `628123`, `08123` — and the form
 * would otherwise show a number that fails to match the customer on submit.
 */
export function splitPhone(phoneNo: string | null | undefined): { prefix: string, number: string } {
  const value = (phoneNo || '').replace(/[\s-()]/g, '')
  if (!value) return { prefix: '+62', number: '' }

  for (const prefix of KNOWN_PHONE_PREFIXES) {
    const digits = prefix.replace('+', '')
    if (value.startsWith(prefix)) return { prefix, number: value.slice(prefix.length) }
    if (value.startsWith(digits)) return { prefix, number: value.slice(digits.length) }
  }

  if (value.startsWith('0')) {
    return { prefix: '+62', number: value.replace(/^0+/, '') }
  }

  return { prefix: '+62', number: value.replace(/\D/g, '') }
}

/**
 * Maps a resolved customer onto the booking form's field names, carrying over what
 * they told us last time.
 *
 * The services and the date are deliberately left alone — those are the choices
 * they came here to make.
 */
export function mapCustomerQrToForm(customer: CustomerQr): Record<string, unknown> {
  const { prefix, number } = splitPhone(customer.phoneNo)
  const last = customer.lastBooking

  const fields: Record<string, unknown> = {
    customerName: customer.name || '',
    customerEmail: customer.email || '',
    // Where they last asked to be seen beats the address sitting on their profile.
    attendedAddress: last?.attendedAddress || customer.address || '',
    customerPhonePrefix: prefix,
    customerPhoneNo: number,
  }

  if (last) {
    fields.remark = last.remark || ''
    // The form holds this as a string, since it is driven by a pair of radios.
    fields.isExternal = last.isExternal ? 'true' : 'false'
    fields.customForm = last.customForm || {}
  }

  return fields
}

/**
 * The same customer mapped onto the rental form, which only ever wants who they are.
 *
 * Nothing from `lastBooking` is carried over: what they asked a stylist for says nothing
 * about the item they are here to rent, and the address a rental needs is where they live
 * rather than where they were last attended.
 */
export function mapCustomerQrToRentForm(customer: CustomerQr): Record<string, string> {
  const { prefix, number } = splitPhone(customer.phoneNo)

  return {
    customerName: customer.name || '',
    customerEmail: customer.email || '',
    customerAddress: customer.address || '',
    customerPhonePrefix: prefix,
    customerPhoneNo: number,
  }
}

/** Reads the opaque code out of a scanned booking URL, or accepts a bare code. */
export function extractQrCode(scanned: string | null | undefined): string | null {
  const value = (scanned || '').trim()
  if (!value) return null

  const fromQuery = value.match(/[?&]c=([A-Za-z0-9]+)/)
  if (fromQuery) return fromQuery[1]

  return /^[A-Za-z0-9]{16,64}$/.test(value) ? value : null
}

/**
 * Decodes a QR from an uploaded image entirely in the browser, so the photo — which
 * may show more than the QR — never leaves the device.
 */
export async function decodeQrFromImageFile(file: File): Promise<string | null> {
  const objectUrl = URL.createObjectURL(file)

  try {
    const image = await loadImage(objectUrl)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return null

    // Cap the working size: phone photos are far larger than jsQR needs and
    // decoding a 12MP bitmap blocks the main thread for seconds.
    const scale = Math.min(1, 1000 / Math.max(image.width, image.height))
    canvas.width = Math.round(image.width * scale)
    canvas.height = Math.round(image.height * scale)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)

    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height)
    const result = jsQR(data, width, height)

    return extractQrCode(result?.data)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Could not read the image'))
    image.src = src
  })
}
